import React, { useEffect, useState } from "react";

import {
    HeartOutlined,
    RiseOutlined,
    FieldTimeOutlined,
    CheckSquareOutlined,
    DashboardOutlined,
    LineChartOutlined,
    RightOutlined,
    MedicineBoxOutlined,
    UserOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined
} from "@ant-design/icons";

import {
    canSyncPatientProfile,
    getPatientProfiles,
    createPatientProfile,
    updatePatientProfile,
    deletePatientProfile,
    getActivePatientProfileId,
    setActivePatientProfileId,
    clearActivePatientProfile
} from "../../api/patientProfile";


export default function TienIchPhucHoiChucNang() {

    const [profiles, setProfiles] =
        useState([]);

    const [activeProfileId, setActiveProfileId] =
        useState(null);

    const [loadingProfiles, setLoadingProfiles] =
        useState(true);

    const [showCreate, setShowCreate] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [message, setMessage] =
        useState("");

    const [newProfile, setNewProfile] =
        useState({
            profile_name: "",
            relationship: "",
            note: ""
        });

    const [editingId, setEditingId] =
        useState(null);

    const [editProfile, setEditProfile] =
        useState({
            profile_name: "",
            relationship: "",
            note: ""
        });


    const tools = [
        {
            name: "Nhật ký đau",
            description:
                "Ghi lại mức độ đau, vị trí đau và theo dõi sự thay đổi theo thời gian",
            icon: <HeartOutlined />,
            color: "bg-red-50 text-red-500",
            link: "/tienich/phuc-hoi-chuc-nang/nhat-ky-dau"
        },
        {
            name: "Theo dõi ROM",
            description:
                "Ghi lại tầm vận động khớp để theo dõi tiến triển phục hồi",
            icon: <RiseOutlined />,
            color: "bg-blue-50 text-blue-600",
            link: "/tienich/phuc-hoi-chuc-nang/rom"
        },
        {
            name: "Barthel Index",
            description:
                "Công cụ hỗ trợ ghi nhận mức độ độc lập trong hoạt động hằng ngày",
            icon: <CheckSquareOutlined />,
            color: "bg-green-50 text-green-600",
            link: "/tienich/phuc-hoi-chuc-nang/barthel"
        },
        {
            name: "Berg Balance Scale",
            description:
                "Hỗ trợ ghi nhận kết quả đánh giá thăng bằng chức năng",
            icon: <DashboardOutlined />,
            color: "bg-purple-50 text-purple-600",
            link: "/tienich/phuc-hoi-chuc-nang/berg"
        },
        {
            name: "Timed Up and Go",
            description:
                "Ghi lại thời gian thực hiện bài kiểm tra vận động TUG",
            icon: <FieldTimeOutlined />,
            color: "bg-orange-50 text-orange-500",
            link: "/tienich/phuc-hoi-chuc-nang/tug"
        },
        {
            name: "Đánh giá cơ lực MRC",
            description:
                "Hỗ trợ ghi lại mức cơ lực theo thang điểm MRC",
            icon: <LineChartOutlined />,
            color: "bg-pink-50 text-pink-500",
            link: "/tienich/phuc-hoi-chuc-nang/mrc"
        }
    ];


    /*
     * LOAD DANH SÁCH HỒ SƠ
     */
    const loadProfiles = async () => {

        setLoadingProfiles(true);
        setMessage("");

        try {

            if (!canSyncPatientProfile()) {

                setProfiles([]);
                setActiveProfileId(null);

                return;
            }


            const data =
                await getPatientProfiles();


            const list =
                Array.isArray(data)
                    ? data
                    : [];


            setProfiles(list);


            const savedId =
                getActivePatientProfileId();


            const savedExists =
                list.some(
                    (item) =>
                        Number(item.profile_id) ===
                        Number(savedId)
                );


            if (savedId && savedExists) {

                setActiveProfileId(
                    Number(savedId)
                );

            } else if (list.length > 0) {

                const firstId =
                    Number(
                        list[0].profile_id
                    );


                setActiveProfileId(
                    firstId
                );


                setActivePatientProfileId(
                    firstId
                );

            } else {

                setActiveProfileId(null);

                clearActivePatientProfile();
            }

        } catch (error) {

            console.error(
                "Lỗi tải hồ sơ:",
                error
            );

            setMessage(
                "Không thể tải danh sách hồ sơ."
            );

        } finally {

            setLoadingProfiles(false);
        }
    };


    useEffect(() => {

        loadProfiles();

    }, []);


    /*
     * CHỌN HỒ SƠ
     */
    const handleSelectProfile = (
        profileId
    ) => {

        const id =
            Number(profileId);


        setActiveProfileId(id);

        setActivePatientProfileId(id);

        setMessage("");
    };


    /*
     * TẠO HỒ SƠ
     */
    const handleCreateProfile = async () => {

        const name =
            newProfile.profile_name.trim();


        if (!name) {

            setMessage(
                "Vui lòng nhập tên hồ sơ."
            );

            return;
        }


        setSaving(true);
        setMessage("");


        try {

            await createPatientProfile({
                profile_name: name,

                relationship:
                    newProfile.relationship.trim(),

                note:
                    newProfile.note.trim()
            });


            setNewProfile({
                profile_name: "",
                relationship: "",
                note: ""
            });


            setShowCreate(false);


            await loadProfiles();


            setMessage(
                "Đã tạo hồ sơ mới."
            );

        } catch (error) {

            console.error(
                "Lỗi tạo hồ sơ:",
                error
            );

            setMessage(
                error?.message ||
                "Không thể tạo hồ sơ."
            );

        } finally {

            setSaving(false);
        }
    };


    /*
     * BẮT ĐẦU SỬA
     */
    const startEdit = (
        profile
    ) => {

        setEditingId(
            profile.profile_id
        );


        setEditProfile({
            profile_name:
                profile.profile_name || "",

            relationship:
                profile.relationship || "",

            note:
                profile.note || ""
        });


        setMessage("");
    };


    /*
     * HỦY SỬA
     */
    const cancelEdit = () => {

        setEditingId(null);

        setEditProfile({
            profile_name: "",
            relationship: "",
            note: ""
        });
    };


    /*
     * LƯU SỬA
     */
    const handleUpdateProfile = async (
        profileId
    ) => {

        const name =
            editProfile.profile_name.trim();


        if (!name) {

            setMessage(
                "Vui lòng nhập tên hồ sơ."
            );

            return;
        }


        setSaving(true);
        setMessage("");


        try {

            await updatePatientProfile(
                profileId,
                {
                    profile_name:
                        name,

                    relationship:
                        editProfile.relationship.trim(),

                    note:
                        editProfile.note.trim()
                }
            );


            setEditingId(null);


            await loadProfiles();


            setMessage(
                "Đã cập nhật hồ sơ."
            );

        } catch (error) {

            console.error(
                "Lỗi cập nhật hồ sơ:",
                error
            );

            setMessage(
                error?.message ||
                "Không thể cập nhật hồ sơ."
            );

        } finally {

            setSaving(false);
        }
    };


    /*
     * XÓA HỒ SƠ
     */
    const handleDeleteProfile = async (
        profile
    ) => {

        const confirmed =
            window.confirm(
                `Bạn có chắc muốn xóa hồ sơ "${profile.profile_name}"?`
            );


        if (!confirmed) {
            return;
        }


        setSaving(true);
        setMessage("");


        try {

            await deletePatientProfile(
                profile.profile_id
            );


            if (
                Number(activeProfileId) ===
                Number(profile.profile_id)
            ) {

                clearActivePatientProfile();

                setActiveProfileId(null);
            }


            await loadProfiles();


            setMessage(
                "Đã xóa hồ sơ."
            );

        } catch (error) {

            console.error(
                "Lỗi xóa hồ sơ:",
                error
            );

            setMessage(
                error?.message ||
                "Không thể xóa hồ sơ."
            );

        } finally {

            setSaving(false);
        }
    };


    const activeProfile =
        profiles.find(
            (item) =>
                Number(item.profile_id) ===
                Number(activeProfileId)
        );


    return (

        <div className="max-w-screen-2xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-8">


            {/* ================== PHẦN ĐẦU ================== */}

            <div className="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl md:text-3xl">

                        <MedicineBoxOutlined />

                    </div>


                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">

                            TIỆN ÍCH Y KHOA

                        </p>


                        <h1 className="text-[25px] md:text-3xl font-bold mt-1">

                            Phục hồi chức năng

                        </h1>

                    </div>

                </div>


                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal max-w-3xl">

                    Các công cụ hỗ trợ ghi lại quá trình phục hồi,
                    theo dõi chức năng vận động và lưu kết quả một số
                    thang đánh giá thường dùng.

                </p>

            </div>


            {/* ================== HỒ SƠ THEO DÕI ================== */}

            <div className="mt-6">

                <div className="flex items-center justify-between gap-3">

                    <div>

                        <h2 className="text-[21px] md:text-2xl font-bold text-gray-800">

                            Hồ sơ đang theo dõi

                        </h2>


                        <p className="text-gray-500 text-[13px] md:text-base mt-1">

                            Lưu dữ liệu riêng cho bản thân, người nhà hoặc từng người được theo dõi.

                        </p>

                    </div>


                    {canSyncPatientProfile() && (

                        <button
                            type="button"
                            onClick={() =>
                                setShowCreate(true)
                            }
                            className="shrink-0 bg-emerald-600 text-white rounded-xl px-3 py-2.5 text-[13px] md:text-sm font-semibold"
                        >

                            <PlusOutlined className="mr-1" />

                            Thêm hồ sơ

                        </button>

                    )}

                </div>


                {/* CHƯA ĐĂNG NHẬP */}

                {!canSyncPatientProfile() && (

                    <div className="mt-4 bg-amber-50 border border-amber-100 rounded-2xl p-4">

                        <div className="flex items-start">

                            <UserOutlined className="text-amber-500 text-xl mt-0.5" />


                            <div className="ml-3">

                                <p className="font-bold text-gray-800">

                                    Đăng nhập để sử dụng hồ sơ

                                </p>


                                <p className="text-[13px] text-gray-600 leading-5 mt-1">

                                    Khi đăng nhập, bạn có thể tạo nhiều hồ sơ
                                    riêng như bản thân, người nhà hoặc người
                                    đang được theo dõi.

                                </p>

                            </div>

                        </div>

                    </div>

                )}


                {/* ĐANG LOAD */}

                {canSyncPatientProfile() &&
                    loadingProfiles && (

                        <div className="mt-4 bg-white border rounded-2xl p-5 text-center text-gray-500 text-sm">

                            Đang tải hồ sơ...

                        </div>

                    )}


                {/* CHƯA CÓ HỒ SƠ */}

                {canSyncPatientProfile() &&
                    !loadingProfiles &&
                    profiles.length === 0 && (

                        <div className="mt-4 bg-white border rounded-2xl p-5">

                            <div className="flex items-start">

                                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">

                                    <UserOutlined />

                                </div>


                                <div className="ml-3 flex-1">

                                    <p className="font-bold text-gray-800">

                                        Chưa có hồ sơ theo dõi

                                    </p>


                                    <p className="text-[13px] text-gray-500 leading-5 mt-1">

                                        Tạo hồ sơ đầu tiên để dữ liệu phục hồi
                                        chức năng được lưu riêng cho từng người.

                                    </p>


                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowCreate(true)
                                        }
                                        className="mt-3 bg-emerald-600 text-white rounded-xl px-4 py-2 text-sm font-semibold"
                                    >

                                        <PlusOutlined className="mr-1" />

                                        Tạo hồ sơ đầu tiên

                                    </button>

                                </div>

                            </div>

                        </div>

                    )}


                {/* HỒ SƠ ĐANG CHỌN */}

                {canSyncPatientProfile() &&
                    !loadingProfiles &&
                    activeProfile && (

                        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">

                            <p className="text-[11px] font-bold text-emerald-700 uppercase">

                                Đang theo dõi

                            </p>


                            <div className="flex items-center mt-2">

                                <div className="w-11 h-11 rounded-full bg-white text-emerald-600 flex items-center justify-center text-xl border border-emerald-100">

                                    <UserOutlined />

                                </div>


                                <div className="ml-3 min-w-0">

                                    <p className="font-bold text-gray-900 text-[16px]">

                                        {activeProfile.profile_name}

                                    </p>


                                    {activeProfile.relationship && (

                                        <p className="text-[12px] text-gray-500 mt-0.5">

                                            {activeProfile.relationship}

                                        </p>

                                    )}

                                </div>

                            </div>

                        </div>

                    )}


                {/* DANH SÁCH HỒ SƠ */}

                {canSyncPatientProfile() &&
                    !loadingProfiles &&
                    profiles.length > 0 && (

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">

                            {profiles.map(
                                (profile) => {

                                    const isActive =
                                        Number(activeProfileId) ===
                                        Number(profile.profile_id);


                                    const isEditing =
                                        Number(editingId) ===
                                        Number(profile.profile_id);


                                    return (

                                        <div
                                            key={profile.profile_id}
                                            className={`rounded-2xl border p-4 ${
                                                isActive
                                                    ? "bg-emerald-50 border-emerald-300"
                                                    : "bg-white"
                                            }`}
                                        >

                                            {isEditing ? (

                                                <div>

                                                    <input
                                                        type="text"
                                                        value={
                                                            editProfile.profile_name
                                                        }
                                                        onChange={(e) =>
                                                            setEditProfile({
                                                                ...editProfile,
                                                                profile_name:
                                                                    e.target.value
                                                            })
                                                        }
                                                        placeholder="Tên hồ sơ"
                                                        className="w-full border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                                                    />


                                                    <input
                                                        type="text"
                                                        value={
                                                            editProfile.relationship
                                                        }
                                                        onChange={(e) =>
                                                            setEditProfile({
                                                                ...editProfile,
                                                                relationship:
                                                                    e.target.value
                                                            })
                                                        }
                                                        placeholder="Mối quan hệ"
                                                        className="w-full border rounded-xl px-3 py-2.5 text-sm mt-2 outline-none focus:border-emerald-500"
                                                    />


                                                    <textarea
                                                        value={
                                                            editProfile.note
                                                        }
                                                        onChange={(e) =>
                                                            setEditProfile({
                                                                ...editProfile,
                                                                note:
                                                                    e.target.value
                                                            })
                                                        }
                                                        placeholder="Ghi chú"
                                                        rows={2}
                                                        className="w-full border rounded-xl px-3 py-2.5 text-sm mt-2 outline-none focus:border-emerald-500 resize-none"
                                                    />


                                                    <div className="flex gap-2 mt-3">

                                                        <button
                                                            type="button"
                                                            disabled={saving}
                                                            onClick={() =>
                                                                handleUpdateProfile(
                                                                    profile.profile_id
                                                                )
                                                            }
                                                            className="flex-1 bg-emerald-600 text-white rounded-xl py-2 text-sm font-semibold"
                                                        >

                                                            <CheckOutlined className="mr-1" />

                                                            Lưu

                                                        </button>


                                                        <button
                                                            type="button"
                                                            onClick={
                                                                cancelEdit
                                                            }
                                                            className="px-4 bg-gray-100 text-gray-600 rounded-xl py-2 text-sm"
                                                        >

                                                            <CloseOutlined />

                                                        </button>

                                                    </div>

                                                </div>

                                            ) : (

                                                <>

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleSelectProfile(
                                                                profile.profile_id
                                                            )
                                                        }
                                                        className="w-full text-left"
                                                    >

                                                        <div className="flex items-center">

                                                            <div
                                                                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                                    isActive
                                                                        ? "bg-emerald-600 text-white"
                                                                        : "bg-gray-100 text-gray-500"
                                                                }`}
                                                            >

                                                                {isActive
                                                                    ? <CheckOutlined />
                                                                    : <UserOutlined />
                                                                }

                                                            </div>


                                                            <div className="ml-3 min-w-0 flex-1">

                                                                <p className="font-bold text-gray-900 truncate">

                                                                    {profile.profile_name}

                                                                </p>


                                                                <p className="text-[12px] text-gray-500 mt-0.5 truncate">

                                                                    {profile.relationship ||
                                                                        "Hồ sơ theo dõi"}

                                                                </p>

                                                            </div>

                                                        </div>

                                                    </button>


                                                    <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t">

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                startEdit(
                                                                    profile
                                                                )
                                                            }
                                                            className="text-[12px] text-blue-600 px-2 py-1"
                                                        >

                                                            <EditOutlined className="mr-1" />

                                                            Sửa

                                                        </button>


                                                        <button
                                                            type="button"
                                                            disabled={saving}
                                                            onClick={() =>
                                                                handleDeleteProfile(
                                                                    profile
                                                                )
                                                            }
                                                            className="text-[12px] text-red-500 px-2 py-1"
                                                        >

                                                            <DeleteOutlined className="mr-1" />

                                                            Xóa

                                                        </button>

                                                    </div>

                                                </>

                                            )}

                                        </div>

                                    );
                                }
                            )}

                        </div>

                    )}


                {/* THÔNG BÁO */}

                {message && (

                    <div className="mt-3 bg-gray-50 border rounded-xl px-3 py-2 text-[13px] text-gray-600">

                        {message}

                    </div>

                )}

            </div>


            {/* ================== FORM TẠO HỒ SƠ ================== */}

            {showCreate && (

                <div className="mt-4 bg-white border border-emerald-200 rounded-2xl p-4 md:p-5 shadow-sm">

                    <div className="flex items-center justify-between">

                        <h3 className="font-bold text-gray-900 text-lg">

                            Thêm hồ sơ theo dõi

                        </h3>


                        <button
                            type="button"
                            onClick={() =>
                                setShowCreate(false)
                            }
                            className="w-8 h-8 rounded-full bg-gray-100 text-gray-500"
                        >

                            <CloseOutlined />

                        </button>

                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">

                        <div>

                            <label className="text-[13px] font-semibold text-gray-700">

                                Tên hồ sơ *

                            </label>


                            <input
                                type="text"
                                value={
                                    newProfile.profile_name
                                }
                                onChange={(e) =>
                                    setNewProfile({
                                        ...newProfile,
                                        profile_name:
                                            e.target.value
                                    })
                                }
                                placeholder="VD: Mẹ, Ba, Bệnh nhân A..."
                                className="w-full border rounded-xl px-3 py-3 text-sm mt-1.5 outline-none focus:border-emerald-500"
                            />

                        </div>


                        <div>

                            <label className="text-[13px] font-semibold text-gray-700">

                                Mối quan hệ / Nhóm

                            </label>


                            <input
                                type="text"
                                value={
                                    newProfile.relationship
                                }
                                onChange={(e) =>
                                    setNewProfile({
                                        ...newProfile,
                                        relationship:
                                            e.target.value
                                    })
                                }
                                placeholder="VD: Bản thân, Người nhà, Người được theo dõi..."
                                className="w-full border rounded-xl px-3 py-3 text-sm mt-1.5 outline-none focus:border-emerald-500"
                            />

                        </div>

                    </div>


                    <div className="mt-3">

                        <label className="text-[13px] font-semibold text-gray-700">

                            Ghi chú

                        </label>


                        <textarea
                            value={
                                newProfile.note
                            }
                            onChange={(e) =>
                                setNewProfile({
                                    ...newProfile,
                                    note:
                                        e.target.value
                                })
                            }
                            placeholder="Thông tin ghi nhớ thêm nếu cần"
                            rows={3}
                            className="w-full border rounded-xl px-3 py-3 text-sm mt-1.5 outline-none focus:border-emerald-500 resize-none"
                        />

                    </div>


                    <button
                        type="button"
                        disabled={saving}
                        onClick={
                            handleCreateProfile
                        }
                        className="w-full md:w-auto bg-emerald-600 text-white rounded-xl px-5 py-3 mt-4 font-semibold text-sm disabled:opacity-60"
                    >

                        <PlusOutlined className="mr-1" />

                        {saving
                            ? "Đang lưu..."
                            : "Tạo hồ sơ"
                        }

                    </button>

                </div>

            )}


            {/* ================== CÔNG CỤ ================== */}

            <div className="mt-8">

                <h2 className="text-[21px] md:text-2xl font-bold text-gray-800">

                    Công cụ phục hồi chức năng

                </h2>


                <p className="text-gray-500 text-[14px] md:text-base mt-1">

                    Chọn công cụ bạn muốn sử dụng

                </p>


                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-4 mt-4">

                    {tools.map(
                        (tool, index) => (

                            <a
                                key={index}
                                href={tool.link}
                                className="bg-white border rounded-2xl p-3 md:p-5 cursor-pointer hover:shadow-md block text-black"
                            >

                                <div
                                    className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-xl md:text-2xl ${tool.color}`}
                                >

                                    {tool.icon}

                                </div>


                                <div className="mt-3">

                                    <div className="flex items-start justify-between">

                                        <h3 className="font-bold text-[14px] md:text-lg leading-5 text-gray-900">

                                            {tool.name}

                                        </h3>


                                        <RightOutlined className="text-gray-300 text-xs mt-1 ml-2" />

                                    </div>


                                    <p className="text-gray-500 text-[11px] md:text-sm leading-4 md:leading-5 mt-1.5">

                                        {tool.description}

                                    </p>

                                </div>

                            </a>

                        )
                    )}

                </div>

            </div>


            {/* ================== NHÓM SỬ DỤNG ================== */}

            <div className="mt-8">

                <h2 className="text-[21px] md:text-2xl font-bold text-gray-800">

                    Hỗ trợ theo dõi quá trình phục hồi

                </h2>


                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">

                    <div className="bg-white border rounded-2xl p-4">

                        <p className="font-bold text-gray-800">

                            Người đang tập phục hồi

                        </p>


                        <p className="text-[13px] text-gray-500 leading-5 mt-1">

                            Lưu lại mức đau, tầm vận động và quá trình thay đổi
                            trong thời gian tập luyện.

                        </p>

                    </div>


                    <div className="bg-white border rounded-2xl p-4">

                        <p className="font-bold text-gray-800">

                            Nhân viên y tế

                        </p>


                        <p className="text-[13px] text-gray-500 leading-5 mt-1">

                            Hỗ trợ ghi nhận một số kết quả đánh giá chức năng
                            trong quá trình theo dõi.

                        </p>

                    </div>


                    <div className="bg-white border rounded-2xl p-4">

                        <p className="font-bold text-gray-800">

                            Sinh viên Y khoa

                        </p>


                        <p className="text-[13px] text-gray-500 leading-5 mt-1">

                            Thuận tiện tham khảo cấu trúc và cách ghi nhận các
                            thang đánh giá phục hồi chức năng.

                        </p>

                    </div>

                </div>

            </div>


            {/* ================== LƯU Ý ================== */}

            <div className="mt-8 mb-4">

                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 md:p-5">

                    <p className="font-bold text-gray-800">

                        Lưu ý

                    </p>


                    <p className="text-[13px] md:text-sm text-gray-600 leading-6 mt-1">

                        Các công cụ trên website có mục đích hỗ trợ ghi nhận
                        và theo dõi thông tin. Kết quả không thay thế việc
                        khám, đánh giá hoặc hướng dẫn trực tiếp của nhân viên y tế.

                    </p>

                </div>

            </div>

        </div>
    );
}